import React from 'react';
import { flowRight } from 'lodash';
import {
  ELSPropsFromModalService,
  ELSWithModalService
} from '@els/els-component-modal-react';
import { ELSButton } from '@els/els-component-button-react';
import { Brand } from '../../constants/common.constants';
import { WelcomeModalConfig } from './welcome-disclaimer.config';
import { WELCOME_DISCLAIMER_MODAL_ID } from './welcome-disclaimer-modal.constants';
import { FlexItem } from '../flex/FlexItem.component';
import { FlexLayout } from '../flex/FlexLayout.component';
import { FlexLayoutModifier } from '../flex/flex.constants';

export type WelcomeDisclaimerModalPropsOnly = {
  brand: Brand;
  config: WelcomeModalConfig;
  onAcknowledge: () => void;
}

export type WelcomeDisclaimerModalProps = WelcomeDisclaimerModalPropsOnly
  & ELSPropsFromModalService

export const WelcomeDisclaimerModalComponent = (props: WelcomeDisclaimerModalProps): JSX.Element => {

  const { config } = props;

  const handleAgree = () => {
    props.onAcknowledge();
    props.modalService.closeModal(WELCOME_DISCLAIMER_MODAL_ID);
  };

  return (
    <div className="c-els-modal">
      <div className="c-els-modal__window u-els-width-2o3 u-els-width-1o1@mobile">
        <div>
          <h3>Welcome to {config.productName}!</h3>

          <div className="o-els-container">
            <h4>What is {config.productName}?</h4>
            <p>
              {config.productName} is a conversational tool powered by generative AI and designed to support
              nursing and health professions students on their learning journey by delivering trusted,
              evidence-based responses and content to course-related questions.
            </p>
          </div>

          <div className="o-els-container">
            <h4>What you need to know</h4>
            <p>
              {config.productName} may provide variable results and while {config.productName} only leverages
              authored content from Elsevier textbooks, it also uses AI to generate responses in real-time,
              without human author review. Clinical judgement and discretion should be exercised before acting
              on or applying the information. Every response includes a list of sources used to generate the
              response for your review.
            </p>
            <p>
              <strong>
                Do not enter personal, confidential, or sensitive information into {config.productName}.
              </strong>
            </p>
            <p>
              <strong>We want your feedback! </strong>
              Use the feedback emojis on any response and/or the Submit feedback button. Nursing and health
              professions experts will also be evaluating responses to drive continuous improvements.
            </p>
          </div>

          <div className="o-els-container">
            <h4>Terms and conditions</h4>
            <p>
              By continuing, you agree with our{' '}
              <a
                href={config.termsUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Terms and conditions
              </a>
              {' '}and{' '}
              <a
                href={config.privacyUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacy policy
              </a>
              .
            </p>
          </div>

          <div className="u-els-margin-top-2x">
            <FlexLayout modifiers={[FlexLayoutModifier.RIGHT, FlexLayoutModifier.GUTTERS]}>
              <FlexItem>
                <ELSButton
                  preventAutoPending
                  onClick={handleAgree}
                >
                  I agree
                </ELSButton>
              </FlexItem>
            </FlexLayout>
          </div>
        </div>
      </div>
    </div>
  );
};

const enhance = flowRight(
  ELSWithModalService,
);

const WelcomeDisclaimerModal: React.FC<WelcomeDisclaimerModalPropsOnly> = enhance(WelcomeDisclaimerModalComponent);
export default WelcomeDisclaimerModal;
